import { DOCUMENT } from '@angular/common';
import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/services/api-services/api.service';
import { selectAuth } from 'src/app/store/auth/auth.selector';
import { TokenAuthState } from 'src/app/store/auth/auth.state';
import { setRewardSpinner } from 'src/app/store/rewards/rewards.actions';
import {
  checkPaymentEnabledForOrg,
  checkRealMoneyisNull,
  showError,
} from 'src/app/utility/common-logic';
import { ShippingAddressMode } from 'src/app/utility/enum';
import { RemainingLimits } from 'src/app/utility/model/max-redemption';
import { environment } from 'src/environments/environment';
import { OrderProcessComponent } from '../order-process/order-process.component';

interface DialogData {
  cartDetails: any;
  addressChoice: any;
  cartTotalPointsPayable: number;
  cartDeliveryFee: number;
}

@Component({
  selector: 'app-order-confirmation-with-cart',
  templateUrl: './order-confirmation-with-cart.component.html',
  styleUrls: ['./order-confirmation-with-cart.component.scss'],
})
export class OrderConfirmationWithCartComponent implements OnInit {
  auth$: TokenAuthState;
  cartDetails: any;
  addressChoice: any;
  onCartDataChange = new EventEmitter();
  @Output() receivedCartChanges = new EventEmitter<void>();
  cartTotalPointsPayable: number;
  cartDeliveryFee: number;
  deliveryFee: number | null | undefined = 0;
  adminFee: number | null | undefined = 0;
  adminFeeFixed: number | null | undefined = 0;
  adminFeePercentage: number | string | null | undefined = 0;
  amountFromCalculatePoint: number | string | null | undefined = 0;
  balancePointsNeededCart: number | string | null | undefined = 0;
  points: any;
  paymentGateway: string = '';
  remainingLimits: RemainingLimits | null = null;
  isPaymentDisabled: boolean = false;
  loading: boolean = false;

  constructor(
    private store: Store,
    public dialogRef: MatDialogRef<OrderConfirmationWithCartComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private api: ApiService,
    public dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) {}

  async ngOnInit() {
    this.store.pipe(select(selectAuth)).subscribe((auth) => {
      this.auth$ = auth;
    });
    await this.getRedemptionLimits();
    if (this.auth$?.organization_settings?.gateway != null) {
      this.paymentGateway = this.auth$?.organization_settings?.gateway;
    }
    this.cartDetails = this.data.cartDetails;
    this.addressChoice =
      this.data?.addressChoice != null ? this.data?.addressChoice : null;
    this.cartTotalPointsPayable = this.data?.cartTotalPointsPayable
      ? this.data?.cartTotalPointsPayable
      : null;
    this.cartDeliveryFee = this.data?.cartDeliveryFee
      ? this.data?.cartDeliveryFee
      : null;
    // TODO: Either we set the above values or call the below API, not sure why we are doing both
    await this.calculatePoints();
  }

  async getRedemptionLimits() {
    this.loading = true;

    this.api
      .getMaxRedemptionLimit()
      .then(async (response: RemainingLimits) => {
        this.remainingLimits = response ?? {
          redemption_count_limit: 0,
          redemption_point_limit: 0,
          cc_payment_point_limit: 0,
          cc_payment_count_limit: 0,
        };
        await this.validateRedemptionLimits();
      })
      .catch((err: any) => {
        showError(err, this.toastr, this.translate);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  get addressDisplay1() {
    if (
      this.data?.addressChoice?.mode === ShippingAddressMode.physicalAddress
    ) {
      return (
        this.data?.addressChoice?.pa?.address_line_1 +
        ' ' +
        this.data?.addressChoice?.pa?.address_line_2 +
        ' ' +
        this.data.addressChoice.pa?.city +
        ' ' +
        this.data.addressChoice.pa?.province +
        ' ' +
        this.data.addressChoice.pa?.pincode
      );
    } else if (this.data?.addressChoice?.mode === ShippingAddressMode.sfStore) {
      return 'SF Store Self-Collection';
    } else {
      return 'SF Locker Self-Collection';
    }
  }

  get addressDisplay2() {
    if (
      this.data?.addressChoice?.mode === ShippingAddressMode.physicalAddress
    ) {
      return this.data?.addressChoice?.pa?.contact_number;
    } else if (this.data?.addressChoice?.mode === ShippingAddressMode.sfStore) {
      return `${this.data.addressChoice?.sfStore}, ${this.data.addressChoice?.contactNumber}`;
    } else if (
      this.data?.addressChoice?.mode === ShippingAddressMode.sfLocker
    ) {
      return `${this.data.addressChoice?.sfLocker}, ${this.data.addressChoice?.contactNumber}`;
    }
    return '';
  }

  get country() {
    if (
      this.data?.addressChoice?.mode === ShippingAddressMode.physicalAddress
    ) {
      return this.data?.addressChoice?.pa?.country;
    }
    return this.auth$?.organization_details?.country;
  }

  async onShowCartSummary(data: any) {
    this.cartDetails = data;
    await this.calculatePoints();
    this.onCartDataChange.emit(this.data);
  }

  get addressType() {
    if (this.data?.addressChoice?.mode === ShippingAddressMode.sfStore) {
      return 'sf-store';
    } else if (
      this.data?.addressChoice?.mode === ShippingAddressMode.sfLocker
    ) {
      return 'sf-locker';
    }
    return 'address';
  }

  get sfShippingId() {
    if (
      this.data?.addressChoice?.mode === ShippingAddressMode.physicalAddress
    ) {
      return this.data?.addressChoice?.pa?.pk;
    } else if (this.data?.addressChoice?.mode === ShippingAddressMode.sfStore) {
      return this.data?.addressChoice?.sfStore;
    } else if (
      this.data?.addressChoice?.mode === ShippingAddressMode.sfLocker
    ) {
      return this.data?.addressChoice?.sfLocker;
    }
    return null;
  }

  get contactNumber() {
    if (
      this.data?.addressChoice?.mode === ShippingAddressMode.physicalAddress
    ) {
      return this.data?.addressChoice?.pa?.contact_number;
    }
    if (
      this.data?.addressChoice?.mode === ShippingAddressMode.sfStore ||
      this.data?.addressChoice?.mode === ShippingAddressMode.sfLocker
    ) {
      return this.data?.addressChoice?.contactNumber;
    }
    const phoneNumber = this.auth$?.user_details?.phone_number;
    return !phoneNumber ? '-' : phoneNumber;
  }

  get shippingId() {
    if (this.data.addressChoice?.mode === ShippingAddressMode.physicalAddress) {
      return this.data.addressChoice.pa?.pk;
    } else if (this.data.addressChoice?.mode === ShippingAddressMode.sfStore) {
      return this.data.addressChoice?.sfStore;
    } else {
      return this.data.addressChoice?.sfLocker;
    }
  }

  get resultPoints() {
    if (!this.isOnlinePaymentCart) {
      return this.data?.cartDetails?.user_points - this.totalPointsPayable;
    }
    return 0;
  }

  get isPaymentEnabledForOrg(): boolean {
    return checkPaymentEnabledForOrg(this.auth$?.organization_settings);
  }

  async onPlaceOrder() {
    this.store.dispatch(setRewardSpinner({ spinner: true }));
    const postData = {
      address: this.data?.addressChoice?.pa?.pk || null,
      shipping_id: this.sfShippingId,
      address_type: this.addressType,
      contact_number: this.contactNumber,
    };
    await this.api
      .redeemCart(postData)
      .then((res: any) => {
        this.api.updateBayerPoints(res.pk);
        this.dialogRef.close();
        this.dialog.open(OrderProcessComponent, {
          maxWidth: '90vw',
          maxHeight: '90vh',
          disableClose: true,
          data: {
            cartDetails: this.cartDetails,
            responseData: res,
            addressChoice: this.addressChoice,
            address: this.data?.addressChoice?.pa?.pk || null,
            shippingId: this.sfShippingId,
            addressType: this.addressType,
            contactNumber: this.contactNumber,
            pointsDeducted: this.totalPointsPayable,
          },
        });
      })
      .catch((err) => showError(err, this.toastr, this.translate))
      .finally(() => this.store.dispatch(setRewardSpinner({ spinner: false })));
  }

  async calculatePoints() {
    this.store.dispatch(setRewardSpinner({ spinner: true }));
    const postData = {
      gateway: this.paymentGateway,
      is_cart: true,
      shipping_id: this.shippingId,
    };
    await this.api
      .calculatePoints(postData)
      .then((res: any) => {
        this.points = res;
        this.cartDeliveryFee = res.delivery_fee;
        this.cartTotalPointsPayable = res.points;
        this.adminFee = Number(res.service_charge);
        this.adminFeeFixed = res.fix_charge;
        this.adminFeePercentage = res.service_charge_percentage;
        this.amountFromCalculatePoint = res.total_amount;
        this.balancePointsNeededCart = res.amount;
      })
      .catch((err) => showError(err, this.toastr, this.translate))
      .finally(() => this.store.dispatch(setRewardSpinner({ spinner: false })));
  }

  get isOnlinePaymentCart() {
    return (
      this.cartDetails?.amount_payable + Number(this.cartDeliveryFee) >
      this.cartDetails?.user_points
    );
  }

  get totalAmountPayable(): number {
    return Number(this.amountFromCalculatePoint);
  }

  get totalPointsPayable(): number {
    return this.points?.total_amount > 0
      ? this.points?.total_amount
      : this.points?.points;
  }

  get isCartAmountAndTotalPayableEqual() {
    return this.cartDetails?.amount_payable == this.totalPointsPayable
      ? true
      : false;
  }

  async onProceedPayment() {
    const payment_success_url =
      environment.url.basePath + 'my-cart/payment/success';
    const payment_failed_url =
      environment.url.basePath + 'my-cart/payment/failed';
    const amount = this.isOnlinePaymentCart
      ? this.totalAmountPayable
      : this.cartDetails?.amount_payable;
    const body = {
      amount: amount,
      points: this.cartTotalPointsPayable,
      organization: this.auth$?.user_details?.organization_pk,
      gateway: this.paymentGateway,
      currency: this.auth$?.auth.currency,
      payment_success_url: payment_success_url,
      payment_failed_url: payment_failed_url,
      redirect_url: payment_success_url,
      admin_fee: this.adminFee,
      delivery_fee: this.cartDeliveryFee,
      is_redemption: 1,
      user: this.auth$?.user_details?.pk,
    };
    const orderDetails = {
      cartDetails: this.cartDetails,
      redeemableDetails: {
        isOnlinePayment: true,
        totalAmountPayable: amount,
        remainingPoints: this.cartDetails?.user_points,
        admin_fee: this.adminFee,
        delivery_fee: this.cartDeliveryFee,
      },
      user_data: {
        addressChoice: this.data?.addressChoice,
        address: this.data?.addressChoice?.pa?.pk || null,
        shippingId: this.sfShippingId,
        addressType: this.addressType,
        city: this.data.addressChoice.pa?.city || null,
        province: this.data.addressChoice.pa?.province || null,
        country: this.country,
        contactNumber: this.contactNumber,
      },
    };

    localStorage.setItem('orderDetails', JSON.stringify(orderDetails));
    await this.paymentWithExternalSupport(body);
  }

  async paymentWithExternalSupport(body) {
    const token = btoa(localStorage.getItem('token-auth'));
    const amount = btoa(body.amount);
    const paymentGateway = btoa(body.gateway);
    const successUrl = encodeURIComponent(btoa(body.payment_success_url));
    const failedUrl = encodeURIComponent(btoa(body.payment_failed_url));
    const shippingId = this.shippingId ?? null;

    const externalPaymentLink =
      environment.url.app +
      'make-payment?token=' +
      token +
      '&amount=' +
      amount +
      '&payment=' +
      paymentGateway +
      '&success_url=' +
      successUrl +
      '&failed_url=' +
      failedUrl +
      '&is_cart=1' +
      '&shipping_id' +
      shippingId;
    window.location.href = externalPaymentLink;
  }

  get hideIfRealMoneyisNull() {
    return checkRealMoneyisNull(this.cartDetails);
  }

  async validateRedemptionLimits() {
    const isPaymentEnabled = checkPaymentEnabledForOrg(
      this.auth$?.organization_settings,
    );
    const totalQuantity = this.cartDetails.results.reduce((sum, item) => {
      return sum + (item.quantity || 0);
    }, 0);

    if ((this.remainingLimits?.redemption_count_limit ?? 0) < totalQuantity) {
      this.isPaymentDisabled = true;
      return false;
    }

    if (
      (this.cartDetails?.amount_payable ?? 0) <=
      (this.remainingLimits?.redemption_point_limit ?? 0)
    ) {
      this.isPaymentDisabled = false;
      return true;
    }

    if (!isPaymentEnabled) {
      this.isPaymentDisabled = true;
      return false;
    }

    if ((this.remainingLimits?.cc_payment_count_limit ?? 0) < 1) {
      this.isPaymentDisabled = true;
      return false;
    }

    if (
      Number(this.balancePointsNeededCart ?? 0) >
      Number(this.remainingLimits?.cc_payment_point_limit ?? 0)
    ) {
      this.isPaymentDisabled = true;
      return false;
    }
    this.isPaymentDisabled = false;
    return true;
  }
}
